import React, { Dispatch, SetStateAction, useState } from 'react';
import cx from 'classnames';
import { subYears } from 'date-fns';

import {
    dateToShortDate,
    getDateRangeBefore,
    getToday,
    getYesterday,
    locale,
} from '@trqk/utils';

import {
    DateRangePicker,
    DropdownWrapper,
    DropdownOption,
    DropdownOptionButton,
} from '..';
import { QuarterSelector } from './QuarterSelector';

interface Props {
    addClasses?: string;
    borderClasses?: string;
    borderConfig?: DropdownBorderConfig;
    dateRange: MaybeEmptyDateRange;
    disabled?: boolean;
    height?: number;
    initialOption?: DropdownOption<MaybeEmptyDateRange>;
    modal: ModalProviderValue;
    now?: Date;
    resetTimestamp?: number;
    selectedLabel?: string;
    setSelectedLabel?: Dispatch<SetStateAction<string>>;
    setDateRange(value: MaybeEmptyDateRange): void;
    width?: number;
}

export const DateRangeSelectorDropdown = (props: Props): JSX.Element => {
    const {
        addClasses,
        borderClasses,
        borderConfig,
        dateRange,
        disabled = false,
        height = 40,
        modal,
        now = new Date(),
        setDateRange,
        width = 250,
    } = props;
    const initialOption = props?.initialOption ?? {
        label: 'Today',
        value: getToday(now),
    };

    let selectedLabel: string;
    let setSelectedLabel: Dispatch<SetStateAction<string>>;

    if (props.selectedLabel && props.setSelectedLabel) {
        /* eslint-disable prefer-destructuring */
        selectedLabel = props.selectedLabel;
        setSelectedLabel = props.setSelectedLabel;
        /* eslint-enable prefer-destructuring */
    } else {
        [selectedLabel, setSelectedLabel] = useState(initialOption.label);
    }

    const [isExpanded, setIsExpanded] = useState(false);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

    const DateRangePickerModal = ({
        remove,
    }: ModalContentProps<Record<string, never>>): JSX.Element => {
        const [customDateRange, setCustomDateRange] = useState<DateRange>({
            start: dateRange?.start ?? now,
            end: dateRange?.end ?? now,
        });

        return (
            <div
                className={cx(
                    'bg-white rounded-lg max-h-screen overflow-y-auto',
                    addClasses,
                )}
            >
                <DateRangePicker
                    onChange={(value: DateRange): void => {
                        setCustomDateRange(value);
                    }}
                    showDoubleView={true}
                    value={customDateRange}
                />
                <hr />
                <div className="flex justify-between m-4">
                    <button
                        type="button"
                        className="text-sm text-tertiary-300 ml-4"
                        onClick={(): void => {
                            remove({ canceled: true, success: false });
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="bg-primary-500 rounded-full text-white w-20 p-1"
                        onClick={(): void => {
                            if (customDateRange !== null) {
                                const { start: nextStart, end: nextEnd } =
                                    customDateRange;

                                setDateRange({
                                    start: nextStart,
                                    end: nextEnd,
                                });
                                setSelectedLabel(
                                    `${dateToShortDate(
                                        nextStart,
                                    )} - ${dateToShortDate(nextEnd)}`,
                                );
                            }

                            setIsExpanded(false);
                            remove({ canceled: false, success: true });
                        }}
                    >
                        OK
                    </button>
                </div>
            </div>
        );
    };

    return (
        <DropdownWrapper
            borderClasses={borderClasses}
            borderConfig={borderConfig}
            closeOnClickOutside={!isDatePickerOpen}
            disabled={disabled}
            height={height}
            isExpanded={isExpanded}
            setIsExpanded={setIsExpanded}
            selectedLabel={selectedLabel}
            width={width}
        >
            <DropdownOption
                option={{
                    label: locale.enUS.DATE_RANGES.ALL_DATES,
                    value: {
                        start: null,
                        end: null,
                    },
                }}
                handleChange={(
                    option: DropdownOption<EmptyDateRange>,
                ): void => {
                    const value = option.value as EmptyDateRange;

                    setDateRange(value);
                    setSelectedLabel(option.label);
                    setIsExpanded(false);
                }}
                height={height}
            />
            <DropdownOption
                option={{
                    label: locale.enUS.DATE_RANGES.TODAY,
                    value: getToday(now),
                }}
                handleChange={(option: DropdownOption<DateRange>): void => {
                    const value = option.value as DateRange;

                    setDateRange(value);
                    setSelectedLabel(option.label);
                    setIsExpanded(false);
                }}
                height={height}
            />
            <DropdownOption
                option={{
                    label: locale.enUS.DATE_RANGES.YESTERDAY,
                    value: getYesterday(now),
                }}
                handleChange={(option: DropdownOption<DateRange>): void => {
                    const value = option.value as DateRange;

                    setDateRange(value);
                    setSelectedLabel(option.label);
                    setIsExpanded(false);
                }}
                height={height}
            />
            <DropdownOption
                option={{
                    label: locale.enUS.DATE_RANGES.PAST_7_DAYS,
                    value: getDateRangeBefore(now, { days: 7 }),
                }}
                handleChange={(option: DropdownOption<DateRange>): void => {
                    const value = option.value as DateRange;

                    setDateRange(value);
                    setSelectedLabel(option.label);
                    setIsExpanded(false);
                }}
                height={height}
            />
            <DropdownOption
                option={{
                    label: locale.enUS.DATE_RANGES.PAST_30_DAYS,
                    value: getDateRangeBefore(now, { days: 30 }),
                }}
                handleChange={(option: DropdownOption<DateRange>): void => {
                    const value = option.value as DateRange;

                    setDateRange(value);
                    setSelectedLabel(option.label);
                    setIsExpanded(false);
                }}
                height={height}
            />
            <DropdownOption
                option={{
                    label: locale.enUS.DATE_RANGES.PAST_90_DAYS,
                    value: getDateRangeBefore(now, { days: 90 }),
                }}
                handleChange={(option: DropdownOption<DateRange>): void => {
                    const value = option.value as DateRange;

                    setDateRange(value);
                    setSelectedLabel(option.label);
                    setIsExpanded(false);
                }}
                height={height}
            />
            <DropdownOption
                option={{
                    label: locale.enUS.DATE_RANGES.PAST_YEAR,
                    value: {
                        start: subYears(now, 1),
                        end: now,
                    },
                }}
                handleChange={(option: DropdownOption<DateRange>): void => {
                    const value = option.value as DateRange;

                    setDateRange(value);
                    setSelectedLabel(option.label);
                    setIsExpanded(false);
                }}
                height={height}
            />
            <QuarterSelector
                borderConfig={borderConfig}
                handleClick={(): void => {
                    setIsExpanded(false);
                }}
                height={height}
                now={now}
                priorQuarters={4}
                setDateRange={setDateRange}
                setSelectedLabel={setSelectedLabel}
                width={width}
            />
            <DropdownOptionButton
                addClasses="border-t border-tertiary-200 w-full"
                label="Custom"
                handleClick={(): void => {
                    modal.add({
                        Content: DateRangePickerModal,
                        handleClose: (): void => {
                            setIsDatePickerOpen(false);
                        },
                    });
                    setIsDatePickerOpen(true);
                }}
                height={height}
            />
        </DropdownWrapper>
    );
};
